import React from 'react';
import { Container, Box, Typography, Paper, Divider, List, ListItem, ListItemText, Chip, Stack, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { APP_NAME } from '../../config/branding';

export default function Presentation() {
  const { t } = useTranslation();
  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
      <Stack spacing={3} sx={{ mb: 4 }}>
        <Chip color="primary" label={t('legal_presentation.chip')} sx={{ alignSelf: 'flex-start' }} />
        <Typography variant="h3" component="h1">
          {t('legal_presentation.title', { appName: APP_NAME })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('legal_presentation.subtitle')}
        </Typography>
      </Stack>

      <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2, bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { md: '320px 1fr' }, gap: { xs: 2, md: 4 } }}>
          <Box sx={{ position: { md: 'sticky' }, top: { md: 88 }, alignSelf: 'start' }}>
            <Typography variant="overline" color="text.secondary">{t('legal_presentation.summary')}</Typography>
            <List dense>
              <ListItem component="a" href="#intro" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_presentation.sections.intro')} />
              </ListItem>
              <ListItem component="a" href="#objectifs" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_presentation.sections.objectives')} />
              </ListItem>
              <ListItem component="a" href="#features" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_presentation.sections.features')} />
              </ListItem>
              <ListItem component="a" href="#clients" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_presentation.sections.clients')} />
              </ListItem>
              <ListItem component="a" href="#pros" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_presentation.sections.pros')} />
              </ListItem>
              <ListItem component="a" href="#flow" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_presentation.sections.how_it_works')} />
              </ListItem>
              <ListItem component="a" href="#tarifs" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_presentation.sections.pricing')} />
              </ListItem>
              <ListItem component="a" href="#support" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_presentation.sections.support')} />
              </ListItem>
              <ListItem component="a" href="#conformite" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_presentation.sections.compliance')} />
              </ListItem>
              <ListItem component="a" href="#securite" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_presentation.sections.security')} />
              </ListItem>
              <ListItem component="a" href="#contact" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_presentation.sections.contact')} />
              </ListItem>
              <ListItem component="a" href="#accessibilite" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_presentation.sections.accessibility')} />
              </ListItem>
              <ListItem component="a" href="#disponibilite" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_presentation.sections.availability')} />
              </ListItem>
              <ListItem component="a" href="#compatibilite" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_presentation.sections.compatibility')} />
              </ListItem>
              <ListItem component="a" href="#langues" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_presentation.sections.languages')} />
              </ListItem>
              <ListItem component="a" href="#faq" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_presentation.sections.faq')} />
              </ListItem>
            </List>
          </Box>

          <Box>
            <Box id="intro" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_presentation.content.intro_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_presentation.content.intro_text', { appName: APP_NAME })}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="objectifs" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_presentation.content.objectives_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_presentation.content.objectives_text')}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="features" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_presentation.content.features_title')}</Typography>
              <List sx={{ pl: 2 }}>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_presentation.content.features_1')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_presentation.content.features_2')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_presentation.content.features_3')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_presentation.content.features_4')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_presentation.content.features_5')} />
                </ListItem>
              </List>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="clients" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_presentation.content.clients_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_presentation.content.clients_text')}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="pros" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_presentation.content.pros_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_presentation.content.pros_text')}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="flow" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_presentation.content.how_title')}</Typography>
              <List sx={{ pl: 2 }}>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_presentation.content.how_1')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_presentation.content.how_2')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_presentation.content.how_3')} />
                </ListItem>
              </List>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="tarifs" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_presentation.content.pricing_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_presentation.content.pricing_text')}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="support" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_presentation.content.support_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_presentation.content.support_text', { appName: APP_NAME })}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="conformite" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_presentation.content.compliance_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_presentation.content.compliance_text', { appName: APP_NAME })}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="securite" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_presentation.content.security_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_presentation.content.security_text_1', { appName: APP_NAME })} <Link href="/legal/confidentialite">{t('legal_presentation.content.privacy_policy')}</Link>
                {t('legal_presentation.content.security_text_2')} <Link href="/legal/conditions">{t('legal_presentation.content.terms')}</Link> {t('legal_presentation.content.security_text_3')}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="contact">
              <Typography variant="h5" gutterBottom>{t('legal_presentation.content.contact_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_presentation.content.contact_text')} <Link href="mailto:contact@saloone.fr">contact@saloone.fr</Link>.
              </Typography>
            </Box>            <Divider sx={{ my: 3 }} />
            <Box id="accessibilite" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_presentation.content.accessibility_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_presentation.content.accessibility_text', { appName: APP_NAME })}
              </Typography>
            </Box>
            <Divider sx={{ my: 3 }} />
            <Box id="disponibilite" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_presentation.content.availability_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_presentation.content.availability_text')}
              </Typography>
            </Box>
            <Divider sx={{ my: 3 }} />
            <Box id="compatibilite" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_presentation.content.compatibility_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_presentation.content.compatibility_text')}
              </Typography>
            </Box>
            <Divider sx={{ my: 3 }} />
            <Box id="langues" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_presentation.content.languages_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_presentation.content.languages_text', { appName: APP_NAME })}
              </Typography>
            </Box>
            <Divider sx={{ my: 3 }} />
            <Box id="faq" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_presentation.content.faq_title')}</Typography>
              <List sx={{ pl: 2 }}>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_presentation.content.faq_q1')} secondary={t('legal_presentation.content.faq_a1')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_presentation.content.faq_q2')} secondary={t('legal_presentation.content.faq_a2')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_presentation.content.faq_q3')} secondary={t('legal_presentation.content.faq_a3')} />
                </ListItem>
              </List>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}





